var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useMemo } from 'react';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { useCompanyFeatures } from 'app/domains/companies';
import { ChatMessageRole, ChatMessageType } from 'shared/domains/apollo/generated/types';
import { LatestWorkflowCheckDocument } from './LatestWorkflowCheck.gql';
import { useMatchWorkflowsForDocument } from './useMatchWorkflowsForDocument';
import { WorkflowApprovalResultSchema, WorkflowSignatoryResultSchema, } from '../schemas';
const EMPTY_APPROVAL_RESULT = {
    summary: '',
    isApprovalRequired: false,
    approvers: [],
    checkedAt: '',
    isChecked: false,
};
const EMPTY_SIGNATORY_RESULT = {
    summary: '',
    signatories: [],
    checkedAt: '',
    isChecked: false,
};
const FALLBACK_JSON_STRING = '{}';
export const useLatestWorkflowCheck = (documentId) => {
    var _a, _b, _c, _d;
    const { isFeatureAvailable } = useCompanyFeatures();
    const { cache } = useApolloClient();
    const isWorkflowsAvailable = isFeatureAvailable('document-assistant:workflows');
    const _e = useMatchWorkflowsForDocument(documentId), { approvalWorkflows, signatoryWorkflows } = _e, matchingState = __rest(_e, ["approvalWorkflows", "signatoryWorkflows"]);
    const _f = useQuery(LatestWorkflowCheckDocument, {
        skip: !isWorkflowsAvailable,
        variables: {
            documentId,
        },
    }), { data } = _f, workflowsState = __rest(_f, ["data"]);
    const approvalChatData = (_a = data === null || data === void 0 ? void 0 : data.approvalChat[0]) === null || _a === void 0 ? void 0 : _a.items[0];
    const approvalChatResponse = (_b = approvalChatData === null || approvalChatData === void 0 ? void 0 : approvalChatData.content) !== null && _b !== void 0 ? _b : FALLBACK_JSON_STRING;
    const signatoryChatData = (_c = data === null || data === void 0 ? void 0 : data.signatoryChat[0]) === null || _c === void 0 ? void 0 : _c.items[0];
    const signatoryChatResponse = (_d = signatoryChatData === null || signatoryChatData === void 0 ? void 0 : signatoryChatData.content) !== null && _d !== void 0 ? _d : FALLBACK_JSON_STRING;
    const loading = workflowsState.loading || matchingState.loading;
    const error = Boolean(workflowsState.error || matchingState.error);
    const approvalResult = useMemo(() => {
        const { success, data: approvalData } = WorkflowApprovalResultSchema.safeParse(JSON.parse(approvalChatResponse));
        if (!success) {
            return EMPTY_APPROVAL_RESULT;
        }
        return Object.assign(Object.assign({}, approvalData), { isChecked: true, checkedAt: (approvalChatData === null || approvalChatData === void 0 ? void 0 : approvalChatData.createdAt) || '' });
    }, [approvalChatData === null || approvalChatData === void 0 ? void 0 : approvalChatData.createdAt, approvalChatResponse]);
    const signatoryResult = useMemo(() => {
        const { success, data: signatoryData } = WorkflowSignatoryResultSchema.safeParse(JSON.parse(signatoryChatResponse));
        if (!success) {
            return EMPTY_SIGNATORY_RESULT;
        }
        return Object.assign(Object.assign({}, signatoryData), { isChecked: true, checkedAt: (signatoryChatData === null || signatoryChatData === void 0 ? void 0 : signatoryChatData.createdAt) || '' });
    }, [signatoryChatData === null || signatoryChatData === void 0 ? void 0 : signatoryChatData.createdAt, signatoryChatResponse]);
    const getTempCacheId = (str) => `temp-${str}-${documentId}-${new Date().toISOString()}`;
    const getExistingCacheData = () => cache.readQuery({ query: LatestWorkflowCheckDocument, variables: { documentId } });
    const addApprovalResultToCache = (result) => {
        var _a;
        const existing = getExistingCacheData();
        cache.writeQuery({
            query: LatestWorkflowCheckDocument,
            variables: { documentId },
            data: {
                signatoryChat: (_a = existing === null || existing === void 0 ? void 0 : existing.signatoryChat) !== null && _a !== void 0 ? _a : [],
                approvalChat: [
                    {
                        __typename: 'DocumentChat',
                        id: getTempCacheId('approval-chat'),
                        documentId,
                        items: [
                            {
                                __typename: 'ChatMessage',
                                id: getTempCacheId('approval-message'),
                                content: JSON.stringify(result),
                                type: ChatMessageType.WorkflowApprovalCheck,
                                role: ChatMessageRole.Assistant,
                                createdAt: new Date().toISOString(),
                            },
                        ],
                    },
                ],
            },
        });
    };
    const addSignatoryResultToCache = (result) => {
        var _a;
        const existing = getExistingCacheData();
        cache.writeQuery({
            query: LatestWorkflowCheckDocument,
            variables: { documentId },
            data: {
                approvalChat: (_a = existing === null || existing === void 0 ? void 0 : existing.approvalChat) !== null && _a !== void 0 ? _a : [],
                signatoryChat: [
                    {
                        __typename: 'DocumentChat',
                        id: getTempCacheId('signatory-chat'),
                        documentId,
                        items: [
                            {
                                __typename: 'ChatMessage',
                                id: getTempCacheId('signatory-message'),
                                content: JSON.stringify(result),
                                type: ChatMessageType.WorkflowSignatoriesSuggest,
                                role: ChatMessageRole.Assistant,
                                createdAt: new Date().toISOString(),
                            },
                        ],
                    },
                ],
            },
        });
    };
    return {
        approvalResult,
        signatoryResult,
        addApprovalResultToCache,
        addSignatoryResultToCache,
        approvalWorkflows,
        signatoryWorkflows,
        loading,
        error,
    };
};
